import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { useDebounce } from '../hooks/useDebounce';
import { SalonBrowseCard, SalonSearchBar } from '../components/salon-browser';

const GENDER_CATEGORIES = {
  male: ['men'],
  female: ['women', 'nails'],
};

export default function Shops() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const category = searchParams.get('category');
  const { user } = useAuth();

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const fetchShops = async () => {
      setLoading(true);
      try {
        const data = await api.getShops(user?.gender ? null : (category || null));
        setShops(data);
      } catch (err) {
        console.error('Failed to fetch shops:', err);
        setShops([]);
      } finally {
        setLoading(false);
      }
    };
    fetchShops();
  }, [category, user?.gender]);

  const allowedCategories = user?.gender ? GENDER_CATEGORIES[user.gender] : null;
  const shopsForUser = allowedCategories
    ? shops.filter((s) => allowedCategories.includes(s.category))
    : shops;

  const pageTitle = user?.gender
    ? (user.gender === 'male' ? t('shops.menBarbers') : t('shops.womenSalonsNails'))
    : category
      ? (category === 'men' ? t('shops.menBarbers') : category === 'women' ? t('shops.womenSalonsNails') : category === 'nails' ? t('shops.nailStudios') : t('shops.categorySalons', { category }))
      : t('shops.allSalons');

  const filteredShops = shopsForUser.filter((s) => {
    if (!debouncedSearch) return true;
    const q = debouncedSearch.toLowerCase();
    const nameMatch = s.name?.toLowerCase().includes(q);
    const hasLocationMatch = s.locations && s.locations.some((loc) => loc.toLowerCase().includes(q));
    return nameMatch || hasLocationMatch;
  });

  return (
    <div
      className="salon-browser-page"
      style={{
        minHeight: '100vh',
        background: 'var(--bg-base)',
        color: '#ffffff',
        padding: '7rem 1.25rem 5rem',
        fontFamily: "'Roboto', sans-serif",
      }}
    >
      <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
        <header style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center', textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 'clamp(3rem, 6vw, 4rem)',
              letterSpacing: '0.06em',
              color: '#ffffff',
              lineHeight: 1,
              margin: 0,
            }}
          >
            {pageTitle}
          </h1>
          <div style={{ width: '60px', height: '4px', background: 'var(--orange)', borderRadius: '2px', margin: '0.5rem 0' }} />
          <p style={{ color: 'rgba(255, 255, 255, 0.92)', fontSize: '1rem', fontFamily: "'Roboto', sans-serif", maxWidth: '600px' }}>
            Choose a salon from our curated selection to view available branches and services.
          </p>
        </header>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ maxWidth: '600px', width: '100%' }}>
            <SalonSearchBar value={search} onChange={setSearch} />
          </div>
        </div>

        <section aria-label={t('shops.allSalons')}>
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} style={{ height: '400px', borderRadius: '12px', background: 'var(--bg-card)', border: '1px solid var(--border)' }} />
              ))}
            </div>
          ) : filteredShops.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '5rem 2rem',
                border: '1px solid var(--border-hover)',
                borderRadius: '12px',
                background: 'var(--bg-card)',
              }}
            >
              <p
                style={{
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'var(--orange)',
                  marginBottom: '0.5rem',
                }}
              >
                {t('shops.noSalonsFound')}
              </p>
              <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)' }}>
                {search
                  ? t('shops.noResults', { search })
                  : user?.gender
                    ? t('shops.noSalonsInCategory')
                    : t('shops.noSalonsAvailable')}
              </p>
            </div>
          ) : (
            <div
              className="salon-browser-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '1.5rem',
              }}
            >
              {filteredShops.map((shop) => (
                <SalonBrowseCard
                  key={shop.id}
                  shop={shop}
                  t={t}
                  hovered={hoveredCard === shop.id}
                  onHover={setHoveredCard}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
